import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Page from '../components/Page';
import ReactGA from 'react-ga4';
import yaroslavImg from '../content/avatars/yaroslav.webp';
import leonidImg from '../content/avatars/leonid.webp';
import eugeneImg from '../content/avatars/eugene.webp';
import laptopHome from '../content/laptop_home.webp';
import laptopEvent from '../content/laptop_event.webp';
import laptopDashboard from '../content/laptop_dashboard.webp';
import laptopReports from '../content/laptop_reports.webp';
import laptopPlanner from '../content/laptop_planner.webp';
import laptopStaff from '../content/laptop_staff.webp';
import laptopGroup from '../content/laptop_groups.webp';
import phoneEvent from '../content/phone_event.webp';

const TESTIMONIALS = [
    {
        image: yaroslavImg,
        initials: 'YN',
        name: 'Yaroslav Narovchenko',
        role: 'Senior Pastor',
        church: 'Lifeway Baptist Church',
        quote:
            'Scheduly makes it easy to create a schedule for our Sunday services. Before, we would use a mix of Google Sheets, and Viber group chats to coordinate.',
    },
    {
        image: leonidImg,
        initials: 'LK',
        name: 'Leonid Kolesnik',
        role: 'Deacon',
        church: 'Lifeway Baptist Church',
        quote:
            "The Greeters and Ushers team use Scheduly's smart rotations to automatically find coverage.",
    },
    {
        image: eugeneImg,
        initials: 'EY',
        name: 'Eugene Yakovlev',
        role: 'Youth Leader',
        church: 'Lifeway Baptist Church',
        quote:
            "Scheduly helps us track small group attendance, which helps us identify members that we should follow up with.",
    },
];

const FEATURES = [
    {
        icon: '📋',
        name: 'Event Planning',
        description:
            "Build detailed events with worship plans, volunteer lists, and role assignments. Send invitations and track who's coming.",
    },
    {
        icon: '👤',
        name: 'Member Directory',
        description:
            'Keep contact details, roles, and notes for every member in a shared, searchable directory your whole team can access.',
    },
    {
        icon: '📊',
        name: 'Built-In Reports',
        description:
            'Track attendance trends, volunteer rates, and event history. Spot who needs a follow-up before they slip through the cracks.',
    },
    {
        icon: '📱',
        name: 'Member Profiles',
        description:
            'Members get their own view to see upcoming assignments, confirm attendance, and manage their availability — no app install needed.',
    },
    {
        icon: '📄',
        name: 'Event Templates',
        description:
            'Save your service structure as a reusable template — roles, schedule, staff, and all. Spin up a new event in seconds, not hours.',
    },
    {
        icon: '🏷️',
        name: 'Groups',
        description:
            'Organize your people into ministries, committees, or small groups. Assign events, track participation, and build real community.',
    },
    {
        icon: '📣',
        name: 'Mass Alerts',
        description:
            'Need to reach everyone at once? Send a mass SMS or email in one click — perfect for last-minute changes or important announcements.',
    },
    {
        icon: '🖥️',
        name: 'Admin Dashboard',
        description:
            "Get a bird's-eye view of your church's activity — upcoming events, volunteer coverage, recent alerts, and membership at a glance.",
    },
];

export default function HomePage() {
    const featuresRef = useRef(null);

    useEffect(() => {
        ReactGA.send('pageview');
    }, []);

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
            {
                '@type': 'Question',
                name: 'Do my volunteers need to install an app?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'No. Volunteers access everything through a link sent to their phone or email — no downloads, no account creation required.',
                },
            },
            {
                '@type': 'Question',
                name: 'Is this right for a small church?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Absolutely. Plans start at $19.99/mo and work for congregations of any size, from 20 members to several hundred.',
                },
            },
            {
                '@type': 'Question',
                name: 'How long does setup take?',
                acceptedAnswer: {
                    '@type': 'Answer',
                    text: 'Under 15 minutes. Add your team, create your first event, and you\'re live. No training or IT help needed.',
                },
            },
        ],
    };

    return (
        <Page seo={{
            title: 'Scheduly — Church Scheduling & Volunteer Management Software',
            description: 'Stop coordinating volunteers over group chat. Scheduly gives your church one place to plan every service, schedule every volunteer, and reach your whole team automatically. Free 3-month trial.',
            canonical: 'https://scheduly.org/',
            ogTitle: 'Scheduly — Church Scheduling & Volunteer Management Software',
            ogDescription: 'Plan services, schedule volunteers, and send automated reminders — all from one place. Free 3-month trial.',
            twitterCard: 'summary_large_image',
            twitterTitle: 'Scheduly — Church Scheduling & Volunteer Management Software',
            twitterDescription: 'Plan services, schedule volunteers, and send automated reminders — all from one place. Free 3-month trial.',
        }}>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
            </Helmet>
            {/* ① HERO */}
            <section className="bg-gradient-to-b from-indigo-50 via-violet-50 to-white text-center px-6 pt-20 pb-0">
                <span className="inline-flex items-center gap-1.5 bg-indigo-100 text-indigo-700 text-xs font-bold px-4 py-1.5 rounded-full mb-6 tracking-wide">
                    <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full" />
                    Church Scheduling &amp; Planning Software
                </span>

                <h1 className="text-5xl md:text-6xl font-black text-slate-900 leading-[1.1] tracking-tight mb-5">
                    Your church,{' '}
                    <span className="text-indigo-600">running like clockwork</span>
                </h1>

                <p className="text-lg text-slate-500 max-w-lg mx-auto leading-relaxed mb-9">
                    Group chats and spreadsheets weren't built for this. Scheduly was.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 justify-center mb-3">
                    <Link
                        to="/checkout?plan=Clubhouse"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white text-base font-bold px-7 py-3.5 rounded-xl no-underline transition-colors"
                    >
                        Start 3-Month Free Trial →
                    </Link>
                    <button
                        onClick={() =>
                            featuresRef.current?.scrollIntoView({ behavior: 'smooth' })
                        }
                        className="bg-white border border-slate-200 hover:border-slate-300 text-slate-900 text-base font-semibold px-7 py-3.5 rounded-xl transition-colors cursor-pointer"
                    >
                        See Features ↓
                    </button>
                </div>

                <p className="text-xs text-slate-400 mb-6">
                    Plans from $19.99/mo after trial
                </p>

                <div className="flex flex-wrap gap-x-5 gap-y-1.5 justify-center text-sm text-slate-400 mb-12">
                    {[
                        'Available on Mobile & Desktop',
                        '100% money-back guarantee',
                        '3 months free, then cancel anytime',
                    ].map((t) => (
                        <span key={t} className="flex items-center gap-1.5">
                            <span className="text-indigo-500 font-bold">✓</span> {t}
                        </span>
                    ))}
                </div>

                <AppScreenshot />
            </section>

            {/* ② SOCIAL PROOF BAR */}
            <div className="bg-slate-900 px-6 py-6">
                <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 flex-wrap">
                    <span className="text-xs font-bold text-slate-500 uppercase tracking-widest whitespace-nowrap">
                        Trusted by churches
                    </span>
                    <div className="hidden sm:block w-px h-6 bg-slate-800" />
                    <SocialProofQuote
                        quote="Scheduly makes it easy to create a schedule for our Sunday services. Before, we would use a mix of Google Sheets, and Viber group chats to coordinate."
                        author="Yaroslav N., Senior Pastor"
                    />
                    <div className="hidden sm:block w-px h-6 bg-slate-800" />
                    <SocialProofQuote
                        quote="Automatic rotations means that I never have to chase volunteers for coverage anymore."
                        author="Leonid K., Deacon"
                    />
                </div>
            </div>

            {/* ③ STATS */}
            <div className="border-b border-slate-100 px-6 py-14">
                <div className="max-w-3xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-8 text-center">
                    {[
                        { num: '2,000+', label: 'Events tracked' },
                        { num: '20,000+', label: 'Notifications delivered' },
                        { num: '1,000+', label: 'Active users' },
                    ].map(({ num, label }) => (
                        <div key={label}>
                            <p className="text-4xl font-black text-indigo-600 tracking-tight">{num}</p>
                            <p className="text-sm text-slate-500 mt-1.5">{label}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* ④ FEATURES HEADER */}
            <div className="text-center px-6 pt-20 pb-10" ref={featuresRef}>
                <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">
                    Features
                </p>
                <h2 className="text-4xl font-black text-slate-900 tracking-tight leading-tight mb-4">
                    Everything your church needs<br className="hidden sm:block" /> to run smoothly
                </h2>
                <p className="text-base text-slate-500 max-w-lg mx-auto leading-relaxed">
                    From shared calendars to automated scheduling — Scheduly brings your
                    entire team together without the chaos.
                </p>
            </div>

            {/* ④ HERO FEATURE CARDS */}
            <div className="px-6 pb-12 max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-5">
                <HeroFeatureCard
                    eyebrow="Automation"
                    eyebrowColor="#7c3aed"
                    bgFrom="#f5f3ff"
                    bgTo="#ede9fe"
                    title="Smart rotations that run themselves"
                    description="Set up a rotation once and Scheduly handles the rest — automatically assigning volunteers, sending reminders, and filling gaps before you even notice them."
                >
                    <AutomationMockup />
                </HeroFeatureCard>

                <HeroFeatureCard
                    eyebrow="SMS & Email Alerts"
                    eyebrowColor="#2563eb"
                    bgFrom="#eff6ff"
                    bgTo="#dbeafe"
                    title="Reach every member instantly"
                    description="Send service reminders, volunteer requests, or mass announcements via SMS or email — and members can respond without installing anything."
                >
                    <SmsMockup />
                </HeroFeatureCard>

                <HeroFeatureCard
                    eyebrow="Shared Calendar"
                    eyebrowColor="#16a34a"
                    bgFrom="#f0fdf4"
                    bgTo="#dcfce7"
                    title="One calendar for your entire congregation"
                    description="Give your whole church a live, shared view of every service, meeting, and event — so no one is ever out of the loop."
                >
                    <CalendarMockup />
                </HeroFeatureCard>
            </div>

            {/* ⑤ PRODUCT SCREENSHOTS */}
            <section className="bg-slate-50 px-6 py-20">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-10">
                        <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">
                            The App
                        </p>
                        <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight mb-4">
                            See it in action
                        </h2>
                        <p className="text-base text-slate-500 max-w-lg mx-auto leading-relaxed">
                            Everything your team needs, built into one clean, intuitive website available on mobile and desktop.
                        </p>
                    </div>
                    <ScreenshotViewer />
                </div>
            </section>

            {/* ⑥ FEATURES GRID */}
            <div className="px-6 pb-20 max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-4">
                {FEATURES.map((f) => (
                    <FeatureCard key={f.name} {...f} />
                ))}
            </div>

            {/* MID-PAGE CTA */}
            <div className="bg-indigo-600 px-6 py-14 text-center">
                <h2 className="text-2xl font-black text-white tracking-tight mb-3">
                    Ready to bring order to your church?
                </h2>
                <p className="text-indigo-200 text-base mb-7 max-w-sm mx-auto leading-relaxed">
                    Join the churches already running smoothly with Scheduly.
                </p>
                <Link
                    to="/checkout?plan=Clubhouse"
                    className="inline-block bg-white text-indigo-600 text-sm font-bold px-8 py-3.5 rounded-xl no-underline hover:bg-indigo-50 transition-colors"
                >
                    Start 3-Month Free Trial →
                </Link>
                <p className="text-indigo-300 text-sm mt-3">
                    Plans from $19.99/mo
                </p>
            </div>

            {/* ⑦ MOBILE */}
            <section className="px-6 py-20 max-w-5xl mx-auto">
                <div className="flex flex-col md:flex-row items-center gap-12">
                    <div className="flex-1">
                        <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">
                            Mobile
                        </p>
                        <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight mb-4">
                            Your team stays in the loop, anywhere
                        </h2>
                        <p className="text-base text-slate-500 leading-relaxed mb-7">
                            Volunteers get notified, RSVP, and see their schedule right from
                            their phone — no app download required. Works on any device,
                            instantly.
                        </p>
                        <ul className="flex flex-col gap-3">
                            {[
                                'RSVP to events with one tap',
                                'See the full service schedule and who else is serving',
                                'Receive SMS and email reminders automatically',
                                'No app install — works in any browser',
                            ].map((item) => (
                                <li key={item} className="flex items-center gap-3 text-sm text-slate-700">
                                    <span className="w-5 h-5 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 text-xs font-bold shrink-0">
                                        ✓
                                    </span>
                                    {item}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="shrink-0 w-52 md:w-60">
                        <img
                            src={phoneEvent}
                            alt="Scheduly on mobile — event view"
                            className="w-full drop-shadow-2xl"
                            loading="lazy"
                        />
                    </div>
                </div>
            </section>

            {/* ⑧ TESTIMONIALS */}
            <section className="bg-slate-900 px-6 py-20" data-testid="testimonials">
                <div className="max-w-5xl mx-auto">
                    <div className="text-center mb-12">
                        <p className="text-xs font-bold text-indigo-400 uppercase tracking-widest mb-3">
                            Testimonials
                        </p>
                        <h2 className="text-3xl font-black text-slate-100 tracking-tight leading-tight mb-3">
                            What church leaders are saying
                        </h2>
                        <p className="text-base text-slate-400 max-w-md mx-auto leading-relaxed">
                            From small congregations to growing multi-ministry churches —
                            here's how Scheduly is making a difference.
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {TESTIMONIALS.map((t) => (
                            <TestimonialCard key={t.name} {...t} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ⑨ OBJECTION FAQ */}
            <section className="bg-indigo-50 px-6 py-16">
                <div className="max-w-3xl mx-auto">
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-10 text-center">
                        Common questions
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div>
                            <p className="text-sm font-bold text-slate-900 mb-2">
                                Do my volunteers need to install an app?
                            </p>
                            <p className="text-sm text-slate-500 leading-relaxed">
                                No. Volunteers access everything through a link sent to their
                                phone or email — no downloads, no account creation required.
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-900 mb-2">
                                Is this right for a small church?
                            </p>
                            <p className="text-sm text-slate-500 leading-relaxed">
                                Absolutely. Plans start at $19.99/mo and work for congregations
                                of any size, from 20 members to several hundred.
                            </p>
                        </div>
                        <div>
                            <p className="text-sm font-bold text-slate-900 mb-2">
                                How long does setup take?
                            </p>
                            <p className="text-sm text-slate-500 leading-relaxed">
                                Under 15 minutes. Add your team, create your first event, and
                                you're live. No training or IT help needed.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* CONTACT */}
            <section className="px-6 py-16 border-t border-slate-100">
                <div className="max-w-xl mx-auto text-center">
                    <p className="text-xs font-bold text-indigo-600 uppercase tracking-widest mb-3">
                        Get in Touch
                    </p>
                    <h2 className="text-2xl font-black text-slate-900 tracking-tight mb-3">
                        Questions? Want a demo?
                    </h2>
                    <p className="text-base text-slate-500 leading-relaxed mb-6">
                        We'd love to walk you through Scheduly or answer any questions you have. Reach out directly and we'll get back to you quickly.
                    </p>
                    <a
                        href="mailto:schedulyevents@gmail.com"
                        className="inline-flex items-center gap-2 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 font-bold text-sm px-6 py-3 rounded-xl no-underline transition-colors"
                    >
                        ✉ schedulyevents@gmail.com
                    </a>
                </div>
            </section>

            {/* ⑩ GUARANTEE + CTA */}
            <section
                className="px-6 py-24 text-center"
                style={{
                    background: 'linear-gradient(135deg, #f8f7ff 0%, #eff6ff 100%)',
                }}
            >
                <div className="flex flex-wrap justify-center gap-3 mb-6">
                    <span className="inline-flex items-center gap-2 bg-white border-2 border-indigo-500 text-indigo-600 text-sm font-bold px-5 py-2 rounded-full">
                        🎉 3-Month Free Trial
                    </span>
                    <span className="inline-flex items-center gap-2 bg-white border-2 border-green-500 text-green-700 text-sm font-bold px-5 py-2 rounded-full">
                        🛡️ 30-Day Money-Back Guarantee
                    </span>
                </div>
                <h2 className="text-3xl font-black text-slate-900 tracking-tight leading-tight mb-4 max-w-lg mx-auto">
                    Nothing to lose.
                </h2>
                <p className="text-base text-slate-500 max-w-sm mx-auto leading-relaxed mb-8">
                    Use Scheduly free for 3 months. After that, plans start at $19.99/mo.
                    And if you're not satisfied in the first 30 days of billing, we'll
                    refund every penny. No questions.
                </p>
                <Link
                    to="/checkout?plan=Clubhouse"
                    className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white text-base font-bold px-9 py-4 rounded-xl no-underline transition-colors"
                >
                    Start 3-Month Free Trial →
                </Link>
                <p className="text-sm text-slate-400 mt-4">
                    Cancel anytime
                </p>
            </section>
        </Page>
    );
}

/* ─── Sub-components ─────────────────────────────── */

function HeroFeatureCard({ eyebrow, eyebrowColor, bgFrom, bgTo, title, description, children }) {
    return (
        <div
            className="rounded-2xl border border-slate-200 overflow-hidden"
            style={{ background: `linear-gradient(135deg, ${bgFrom}, ${bgTo})` }}
        >
            <div className="p-6 pb-4">
                <p
                    className="text-xs font-bold uppercase tracking-widest mb-2.5"
                    style={{ color: eyebrowColor }}
                >
                    {eyebrow}
                </p>
                <h3 className="text-xl font-black text-slate-900 leading-snug mb-2">
                    {title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
            </div>
            <div className="flex justify-center items-end px-6 pb-0">{children}</div>
        </div>
    );
}

function AutomationMockup() {
    const rows = [
        { role: 'Worship Lead', status: '✓ Auto-assigned', color: '#4ade80' },
        { role: 'Sound Tech', status: '✓ Auto-assigned', color: '#4ade80' },
        { role: 'Greeters (×3)', status: 'Notifying…', color: '#fbbf24' },
        { role: "Children's Staff", status: '✓ Auto-assigned', color: '#4ade80' },
    ];
    return (
        <div className="w-full rounded-t-xl p-3" style={{ background: '#1e1b4b' }}>
            <p className="text-xs text-indigo-300 font-bold mb-2.5">
                ⚡ Auto-Schedule · Apr 5
            </p>
            {rows.map(({ role, status, color }) => (
                <div
                    key={role}
                    className="flex justify-between rounded px-2 py-1.5 mb-1.5 text-xs"
                    style={{ background: 'rgba(255,255,255,0.06)' }}
                >
                    <span className="text-indigo-100">{role}</span>
                    <span style={{ color }}>{status}</span>
                </div>
            ))}
        </div>
    );
}

function SmsMockup() {
    return (
        <div className="w-full rounded-t-xl p-3" style={{ background: '#0f172a' }}>
            <p className="text-xs text-slate-500 text-center mb-2">📱 Scheduly Alert</p>
            <div
                className="text-xs text-white rounded-xl rounded-bl-sm p-2.5 mb-2 leading-relaxed max-w-[75%]"
                style={{ background: '#4f46e5' }}
            >
                Hi John! You're scheduled for Sound Tech this Sunday at 10am. Can you make it?
            </div>
            <div
                className="text-xs text-slate-400 rounded-xl rounded-br-sm p-2.5 mb-1 leading-relaxed max-w-[75%] ml-auto"
                style={{ background: '#1e293b' }}
            >
                Yes, I'll be there! 👍
            </div>
            <p className="text-xs text-right" style={{ color: '#4ade80' }}>
                ✓ Response recorded
            </p>
        </div>
    );
}

function CalendarMockup() {
    const events = [
        { name: 'Sunday Service', time: 'Apr 5 · 10:00 AM · 48 attending' },
        { name: 'Small Groups', time: 'Apr 7 · 7:00 PM · 12 attending' },
        { name: 'Youth Night', time: 'Apr 9 · 6:30 PM' },
    ];
    return (
        <div className="w-full rounded-t-xl p-3" style={{ background: '#1e1b4b' }}>
            <p className="text-xs text-indigo-300 font-bold mb-2.5">📅 April 2026</p>
            {events.map(({ name, time }) => (
                <div
                    key={name}
                    className="rounded px-2 py-1.5 mb-1.5"
                    style={{ background: 'rgba(79,70,229,0.35)' }}
                >
                    <p className="text-xs font-semibold text-indigo-100">{name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{time}</p>
                </div>
            ))}
        </div>
    );
}

const SCREENSHOTS = [
    {
        label: 'Calendar',
        image: laptopHome,
        description: 'A full monthly view of every service, meeting, and event — visible to your whole congregation in real time.',
    },
    {
        label: 'Event Builder',
        image: laptopEvent,
        description: 'Build detailed services with schedules, volunteer rosters, role assignments, and event images.',
    },
    {
        label: 'Volunteer Rotations',
        image: laptopStaff,
        description: 'Schedule volunteers and set up automatic rotations when a volunteer is unavailable.',
    },
    {
        label: 'Planner',
        image: laptopPlanner,
        description: 'Coordinate multiple events all in one screen.',
    },
    {
        label: 'Dashboard',
        image: laptopDashboard,
        description: "Bird's-eye view of your church's activity — events, members, SMS and email usage at a glance.",
    },
    {
        label: 'Reports',
        image: laptopReports,
        description: 'Track attendance trends and spot exactly who needs a follow-up — no spreadsheets required.',
    },
    {
        label: 'Groups',
        image: laptopGroup,
        description: 'Organize your people into ministries, committees, or small groups. Assign events, track participation, and build real community.',
    },
];


// import laptopPlanner from '../content/laptop_planner.png';
// import laptopStaff from '../content/laptop_staff.png';
// import laptopGroup from '../content/laptop_groups.png';
function ScreenshotViewer() {
    const [active, setActive] = useState(2);
    const { image, label, description } = SCREENSHOTS[active];

    return (
        <div>
            <div className="flex flex-wrap justify-center gap-2 mb-8">
                {SCREENSHOTS.map((s, i) => (
                    <button
                        key={s.label}
                        onClick={() => setActive(i)}
                        className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer ${active === i
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white border border-slate-200 text-slate-600 hover:border-indigo-300 hover:text-indigo-600'
                            }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            <div className="rounded-2xl overflow-hidden shadow-2xl shadow-slate-200 border border-slate-200">
                <img src={image} alt={label} className="w-full block" loading="lazy" />
            </div>

            <p className="text-center text-sm text-slate-500 mt-5 leading-relaxed max-w-lg mx-auto">
                {description}
            </p>
        </div>
    );
}

function FeatureCard({ icon, name, description }) {
    return (
        <div className="bg-white border border-slate-200 rounded-xl p-5 hover:border-indigo-200 hover:shadow-lg hover:shadow-indigo-50 transition-all">
            <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-xl mb-3">
                {icon}
            </div>
            <h3 className="text-sm font-bold text-slate-900 mb-1.5">{name}</h3>
            <p className="text-sm text-slate-500 leading-relaxed">{description}</p>
        </div>
    );
}

function TestimonialCard({ image, initials, name, role, church, quote }) {
    return (
        <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
            <div className="text-yellow-400 text-sm mb-3">★★★★★</div>
            <p className="text-sm text-slate-300 leading-relaxed italic mb-5">
                "{quote}"
            </p>
            <div className="flex items-center gap-2.5">
                {image ? (
                    <img
                        src={image}
                        alt={name}
                        className="w-9 h-9 rounded-full object-cover shrink-0"
                    />
                ) : (
                    <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-xs font-black text-white shrink-0">
                        {initials}
                    </div>
                )}
                <div>
                    <p className="text-sm font-bold text-slate-200">{name}</p>
                    <p className="text-xs text-slate-500">{role}{church ? ` · ${church}` : ''}</p>
                </div>
            </div>
        </div>
    );
}

function SocialProofQuote({ quote, author }) {
    return (
        <div className="max-w-xs text-center sm:text-left">
            <p className="text-sm text-slate-400 italic">"{quote}"</p>
            <p className="text-xs text-slate-600 mt-1">— {author}</p>
        </div>
    );
}

function AppScreenshot() {
    const events = [
        {
            name: 'Sunday Morning Service',
            meta: 'Apr 5 · 10:00 AM · 14 volunteers',
            badge: '✓ All covered',
            bg: 'rgba(79,70,229,0.45)',
            badgeColor: '#4ade80',
        },
        {
            name: 'Small Group — Young Adults',
            meta: 'Apr 7 · 7:00 PM · hosted by Eugene Y.',
            badge: '12 RSVPs',
            bg: 'rgba(255,255,255,0.05)',
            badgeColor: '#a5b4fc',
        },
        {
            name: 'Youth Night',
            meta: 'Apr 9 · 6:30 PM · 3 volunteers needed',
            badge: '⏳ Pending',
            bg: 'rgba(255,255,255,0.05)',
            badgeColor: '#fbbf24',
        },
    ];
    const stats = [
        { num: '48', label: 'Members', color: '#a5b4fc' },
        { num: '12', label: 'Events this month', color: '#a5b4fc' },
        { num: '94%', label: 'Attendance rate', color: '#4ade80' },
        { num: '6', label: 'Groups', color: '#a5b4fc' },
    ];
    const sidebarItems = [
        '📅 Calendar',
        '👥 Members',
        '⚡ Planner',
        '📊 Reports',
        '🏷️ Groups',
        '💬 Alerts',
    ];

    return (
        <div
            className="max-w-4xl mx-auto rounded-t-2xl overflow-hidden border border-b-0"
            style={{
                background: '#1e1b4b',
                borderColor: 'rgba(255,255,255,0.1)',
                boxShadow: '0 -8px 60px rgba(79,70,229,0.2)',
            }}
        >
            {/* Chrome bar */}
            <div
                className="flex items-center gap-1.5 px-4 py-2.5"
                style={{ background: '#2d2a5e' }}
            >
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                <span className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
                <div
                    className="flex-1 ml-3 rounded h-5 flex items-center px-2.5"
                    style={{ background: 'rgba(255,255,255,0.1)' }}
                >
                    <span className="text-xs text-white/40">app.scheduly.org</span>
                </div>
            </div>

            {/* App body */}
            <div
                className="flex gap-3 p-5"
                style={{ background: '#0f0d2e' }}
            >
                {/* Sidebar */}
                <div
                    className="w-36 shrink-0 rounded-lg p-3 hidden sm:block"
                    style={{ background: 'rgba(255,255,255,0.04)' }}
                >
                    <div className="text-xs font-black text-indigo-300 mb-3">
                        📅 Scheduly
                    </div>
                    {sidebarItems.map((item, i) => (
                        <div
                            key={item}
                            className="text-xs px-2 py-1.5 rounded mb-1"
                            style={
                                i === 0
                                    ? { background: 'rgba(79,70,229,0.3)', color: '#a5b4fc', fontWeight: 600 }
                                    : { color: '#475569' }
                            }
                        >
                            {item}
                        </div>
                    ))}
                </div>

                {/* Main */}
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-3">
                        <span className="text-sm font-black text-indigo-100">
                            Upcoming Events
                        </span>
                        <span className="text-xs text-slate-500">March 2026</span>
                    </div>

                    <div className="flex flex-col gap-1.5 mb-3">
                        {events.map(({ name, meta, badge, bg, badgeColor }) => (
                            <div
                                key={name}
                                className="flex justify-between items-center rounded-lg px-3 py-2.5"
                                style={{ background: bg, border: '1px solid rgba(255,255,255,0.06)' }}
                            >
                                <div className="min-w-0 mr-3">
                                    <div className="text-xs font-semibold text-indigo-100 truncate">
                                        {name}
                                    </div>
                                    <div className="text-xs mt-0.5 text-slate-500">{meta}</div>
                                </div>
                                <span
                                    className="text-xs font-semibold px-2 py-1 rounded-full shrink-0"
                                    style={{ color: badgeColor, background: `${badgeColor}22` }}
                                >
                                    {badge}
                                </span>
                            </div>
                        ))}
                    </div>

                    <div className="flex gap-2">
                        {stats.map(({ num, label, color }) => (
                            <div
                                key={label}
                                className="flex-1 rounded-lg py-2.5 text-center"
                                style={{ background: 'rgba(255,255,255,0.04)' }}
                            >
                                <div className="text-lg font-black" style={{ color }}>
                                    {num}
                                </div>
                                <div className="text-xs uppercase tracking-wide mt-0.5 text-slate-500 hidden sm:block">
                                    {label}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
